'use client'

import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react'
import { cn } from '@/utils/styles/cn'

/** `m:ss`. Guards the pre-metadata window, where a duration is 0 — or NaN, which used to read `NaN:NaN`. */
const formatTime = (time: number) => {
    if (!Number.isFinite(time) || time <= 0) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export interface VideoPlayerControlsProps {
    isPlaying: boolean
    /** Seconds. The bar speaks seconds on purpose — it must not know which engine is behind it. */
    currentTime: number
    /** Seconds. 0 (or NaN) before the engine knows, which the bar renders as an empty, inert scrubber. */
    duration: number
    /** 0..1. Pass the underlying level, not 0-while-muted — the bar renders the mute itself. */
    volume: number
    isMuted: boolean
    onTogglePlay: (event: React.MouseEvent<HTMLButtonElement>) => void
    onSeek: (seconds: number) => void
    onVolumeChange: (volume: number) => void
    onToggleMute: () => void
    /**
     * The trailing maximize toggle is OPTIONAL and its meaning belongs to whoever mounts the bar — the
     * creative player uses it to let a card hand the video its chrome, the editor uses it for real
     * fullscreen. Omit the handler and the button is not rendered, rather than drawn dead.
     */
    isExpanded?: boolean
    onToggleExpand?: () => void
    className?: string
}

/**
 * The playback control bar, and nothing else: it renders and calls back, it owns no playback. One bar
 * for both engines (an `HTMLVideoElement` and a Remotion `PlayerRef`) so the two cannot drift apart.
 *
 * Two constraints the markup cannot state:
 * - It positions against the nearest positioned ancestor and reveals on `group-hover`, so the mount MUST
 *   wrap it in a `relative group` box **sized to the painted picture** — anchored to a letterboxed
 *   element the bar overhangs by exactly the letterbox, which is the bug this was extracted out of.
 * - Nothing here may transition on the scrub position. It is fed ~4x/s during playback and a CSS
 *   transition on that value smears permanently behind the playhead. (The Slider's thumb transitions
 *   `colors` only — keep it that way.)
 */
export function VideoPlayerControls({
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    onTogglePlay,
    onSeek,
    onVolumeChange,
    onToggleMute,
    isExpanded,
    onToggleExpand,
    className,
}: VideoPlayerControlsProps) {
    // Radix maps a value onto [min, max]; a duration the engine hasn't reported yet must not reach it.
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0
    const safeCurrentTime = Number.isFinite(currentTime) ? Math.min(Math.max(currentTime, 0), safeDuration) : 0

    return (
        <div
            className={cn(
                'absolute bottom-0 left-0 right-0 p-4 bg-linear-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-20',
                className
            )}
        >
            <div className="flex flex-col gap-2">
                <Slider
                    value={[safeCurrentTime]}
                    min={0}
                    max={safeDuration}
                    step={1}
                    onValueChange={(value) => onSeek(value[0])}
                    className="w-full"
                />
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white hover:bg-white/20"
                            onClick={onTogglePlay}
                        >
                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <span className="text-xs text-white">
                            {formatTime(safeCurrentTime)} / {formatTime(safeDuration)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white hover:bg-white/20"
                            onClick={onToggleMute}
                        >
                            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </Button>
                        <Slider
                            value={[isMuted ? 0 : volume]}
                            min={0}
                            max={1}
                            step={0.1}
                            onValueChange={(value) => onVolumeChange(value[0])}
                            className="w-24"
                        />
                        {onToggleExpand && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-white hover:bg-white/20"
                                onClick={onToggleExpand}
                            >
                                {isExpanded ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
